import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from 'shared/contexts/AuthContext';
import { platformSettingsApi } from 'shared/services/platformSettingsApi';
import { useCompany } from 'shared/hooks/queries/useCompanies';

interface ModuleGuardProps {
  module: 'workPermits' | 'marketplace' | 'capacitaciones';
  children: React.ReactNode;
}

export const ModuleGuard: React.FC<ModuleGuardProps> = ({ module, children }) => {
  const { user } = useAuth();
  const location = useLocation();

  // Check if we're in a company context via URL
  const companyMatch = location.pathname.match(/^\/companies\/([a-f0-9]{24})/);
  const urlCompanyId = companyMatch ? companyMatch[1] : null;

  // Platform-level config gates first — if a module is off here, the
  // per-company flag can't override it. This data is slow-changing and
  // admin-only-write, so a long staleTime is safe; the React Query cache
  // also means every gated route re-mount doesn't refetch it (see PERF-3,
  // audit 2026-09-07).
  const { data: platformSettings, isLoading: loadingPlatform } = useQuery({
    queryKey: ['platformSettings'],
    queryFn: () => platformSettingsApi.get(),
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    retry: 1,
  });
  const platformModules = platformSettings?.modules as unknown as Record<string, boolean> | undefined;

  // Platform user viewing another company — reuse the SAME cached query
  // Layout.tsx already fires for the active-company chip on these same
  // routes, instead of an independent, uncached fetch on every mount.
  const isPlatformBrowsing = !!urlCompanyId;
  const { data: viewedCompanyResponse, isLoading: loadingViewedCompany } = useCompany(
    urlCompanyId || '',
    isPlatformBrowsing,
  );

  const companyModules = urlCompanyId
    ? (viewedCompanyResponse?.data?.settings?.modules as unknown as Record<string, boolean> | undefined)
    : (user?.company?.settings?.modules as unknown as Record<string, boolean> | undefined);

  const loading = loadingPlatform || (isPlatformBrowsing && loadingViewedCompany);

  if (loading) return null;

  const fallback = urlCompanyId ? `/companies/${urlCompanyId}` : '/';

  // Platform-level kill switch.
  if (platformModules && platformModules[module] === false) {
    return <Navigate to={fallback} replace />;
  }

  // Company-level toggle.
  if (companyModules && companyModules[module] === false) {
    return <Navigate to={fallback} replace />;
  }

  return <>{children}</>;
};
